/** Versioned event vocabulary shared by the browser and both ingestion endpoints. */
export const ANALYTICS_VERSION = 2;
export const MAX_BATCH_BYTES = 28 * 1024;
export const MAX_REQUEST_BYTES = 32 * 1024;
export const MAX_EVENTS = 50;
export const SOURCES = ['jamcam-web', 'jamcam-app', 'jamcam-app-auth'] as const;
export type AnalyticsSource = (typeof SOURCES)[number];
export type EventProps = Record<string, string | number | boolean>;

const stage = {
  stageSessionId: 'string',
  experience: 'string',
  moduleId: 'string',
} as const;
export const EVENT_FIELDS = {
  page_view: {},
  tab_import_started: {
    importId: 'string',
    sourceType: 'string',
    timing: 'string',
  },
  tab_import_result: {
    importId: 'string',
    outcome: 'string',
    elapsedMs: 'number',
  },
  tab_saved: {
    importId: 'string',
    tabId: 'string',
    measureCount: 'number',
    eventCount: 'number',
  },
  tab_playback_summary: {
    playbackSessionId: 'string',
    contentKind: 'string',
    playedSeconds: 'number',
    completedPassages: 'number',
    reason: 'string',
  },
  tab_practice_action: {
    action: 'string',
    songId: 'string',
    sectionId: 'string',
    speed: 'number',
  },
  section_view: { sectionId: 'string' },
  cta_click: { action: 'string', placement: 'string' },
  learn_family_open: { familyId: 'string', placement: 'string' },
  learn_module_open: {
    moduleId: 'string',
    familyId: 'string',
    placement: 'string',
    access: 'string',
  },
  stage_open: stage,
  camera_start_requested: stage,
  camera_ready: { ...stage, elapsedMs: 'number' },
  camera_failed: { ...stage, reason: 'string' },
  model_ready: { ...stage, elapsedMs: 'number' },
  model_failed: { ...stage, reason: 'string' },
  overlay_visible: { ...stage, elapsedMs: 'number' },
  stage_summary: {
    ...stage,
    visibleSeconds: 'number',
    trackedSeconds: 'number',
    reason: 'string',
  },
  practice_run_completed: { moduleId: 'string' },
  demo_signup_click: { ...stage, placement: 'string', overlaySeen: 'boolean' },
} as const;
export type AnalyticsEventName = keyof typeof EVENT_FIELDS;
export interface AnalyticsEvent {
  schemaVersion: number;
  eventId: string;
  event: AnalyticsEventName;
  source: AnalyticsSource;
  sessionId: string;
  anonId: string;
  ts: string;
  path: string;
  landingPath: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** Opaque, short-lived website→demo correlation token, not an account identity. */
  journeyId?: string;
  props: EventProps;
}

export function validProps(
  name: AnalyticsEventName,
  props: unknown
): props is EventProps {
  if (
    !Object.prototype.hasOwnProperty.call(EVENT_FIELDS, name) ||
    !props ||
    typeof props !== 'object' ||
    Array.isArray(props)
  )
    return false;
  const values = props as Record<string, unknown>;
  const fields: Record<string, string> = {
    ...EVENT_FIELDS[name],
    ...(name === 'practice_run_completed' ? stage : {}),
  };
  if (
    !Object.entries(EVENT_FIELDS[name]).every(
      ([key, type]) =>
        typeof values[key] === type &&
        (type !== 'string' || (values[key] as string).trim().length > 0)
    )
  )
    return false;
  return (
    Object.entries(values).length <= 20 &&
    Object.entries(values).every(
      ([key, value]) =>
        Object.prototype.hasOwnProperty.call(fields, key) &&
        typeof value === fields[key] &&
        key.length <= 64 &&
        (typeof value === 'string'
          ? value.length <= 160
          : typeof value === 'boolean' ||
            (typeof value === 'number' && Number.isFinite(value) && value >= 0))
    )
  );
}
export function cleanPath(value: string): string {
  try {
    return new URL(value, 'https://local.invalid').pathname.slice(0, 512);
  } catch {
    return '/';
  }
}
export function newEventId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2)}-${Math.random().toString(36).slice(2)}`;
}
export function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}
export const validId = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-zA-Z0-9_-]{8,80}$/.test(value);
